/**
 * Generates a scroll handler for carousel markers.
 * Scrolls the carousel to the corresponding slide when a marker is clicked.
 *
 * @param {HTMLElement} carouselElement - The carousel container element.
 * @returns {Function} Event handler for marker click.
 */
export const generateMarkerScrollHandle = (carouselElement) => {
  return function (e) {
    e.preventDefault();
    const targetId = this.getAttribute('href');
    const targetElement = carouselElement.querySelector(targetId);
    // Eventually we will get video elements which are slightly different. In this case, offsetLeft value is 0
    // Therefore, we would need to reach its parent, its offsetLeft won't be zero
    carouselElement.scrollTo({
      left: targetElement.offsetLeft == 0 ? targetElement.parentNode.offsetLeft : targetElement.offsetLeft,
      behavior: 'smooth',
    });
  };
};

/**
 * Generates a scroll handler to update active marker and arrow button states.
 * Updates the active marker and enables/disables navigation arrows based on scroll position.
 *
 * @param {Object} params - Parameters object.
 * @param {HTMLElement} params.carouselElement - The carousel container element.
 * @param {HTMLElement} params.prevBtn - Previous button element.
 * @param {HTMLElement} params.nextBtn - Next button element.
 * @param {HTMLElement[]} params.markersElements - Array of marker elements.
 * @param {string} params.toggleClass - CSS class to toggle for active marker.
 * @returns {Function} Scroll event handler.
 */
export const generateScrollHandle = ({
  carouselElement,
  prevBtn,
  nextBtn,
  markersElements,
  toggleClass,
  extraToggleClass,
}) => {
  return function () {
    // Function to update active marker and disappear/appear arrow buttons
    const scrollLeft = carouselElement.scrollLeft;
    const scrollStepAmount = carouselElement.querySelector('img,div,video,picture').offsetWidth;

    // It will switch dot markers when you are at 50% mark towards the next slide. (not 100% at the next slide.)
    const activeIndex = Math.round(scrollLeft / scrollStepAmount);

    markersElements.forEach((marker, index) => {
      marker.classList.toggle(toggleClass, index === activeIndex);
      extraToggleClass && marker.classList.toggle(extraToggleClass, index === activeIndex);
      if (!prevBtn && !nextBtn) return;
      prevBtn.disabled = 0 === activeIndex;
      nextBtn.disabled = markersElements.length - 1 === activeIndex;
    });
  };
};

/**
 * Generates a handler to scroll the carousel by one step.
 * Scrolls the carousel left or right by one slide when arrow button is clicked.
 *
 * @param {HTMLElement} carouselElement - The carousel container element.
 * @returns {Function} Event handler for step scroll.
 */
export const generateScrollByStepHandle = (carouselElement, gap = 0) => {
  return function (e) {
    const scrollStepAmount = carouselElement.querySelector('img,div').offsetWidth + gap;
    const direction = e.target.hasAttribute('data-right') ? 1 : -1;
    const currentScrollLeft = carouselElement.scrollLeft;
    const activeIndex = Math.round(currentScrollLeft / scrollStepAmount);
    // We're going to calculate next absolute target scrollLeft based on absolute previous scrollLeft instead of relative target scrollLeft
    const previousStepAmount = activeIndex * scrollStepAmount;
    const targetScrollLeft = previousStepAmount + direction * scrollStepAmount;
    // We replaced Element.scrollBy with Element.scrollTo because of bad functionality on newer Safari browsers
    carouselElement.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  };
};

/**
 * Generates a keydown event handler for carousel navigation.
 * Scrolls the carousel left or right when arrow keys are pressed.
 *
 * @param {HTMLElement} carouselElement - The carousel container element.
 * @returns {Function} Keydown event handler.
 */
export const generateKeydownHandle = (carouselElement) => {
  return function (e) {
    const scrollStepAmount = carouselElement.querySelector('img,div').offsetWidth;
    const currentScrollLeft = carouselElement.scrollLeft;
    const activeIndex = Math.round(currentScrollLeft / scrollStepAmount);
    const previousStepAmount = activeIndex * scrollStepAmount;

    // We replaced Element.scrollBy with Element.scrollTo because of bad functionality on newer Safari browsers
    if (e.key === 'ArrowLeft')
      carouselElement.scrollTo({ left: previousStepAmount - scrollStepAmount, behavior: 'smooth' });
    if (e.key === 'ArrowRight')
      carouselElement.scrollTo({ left: previousStepAmount + scrollStepAmount, behavior: 'smooth' });
  };
};

/**
 * Reset carousel scrolling to 0
 *
 * @param {Object} params - The carousel container element.
 * @param {HTMLElement} params.prevBtn - Previous button element.
 * @param {HTMLElement} params.nextBtn - Next button element.
 * @param {HTMLElement[]} params.markersElements - Array of marker elements.
 * @param {string} params.toggleClass - CSS class to toggle for active marker.
 * @param {string} params.extraToggleClass - additional CSS class to toggle for active marker.
 */
export const resetScrollHandle = ({ markersElements, prevBtn, nextBtn, toggleClass, extraToggleClass }) => {
  prevBtn.disabled = true;
  nextBtn.disabled = false;
  markersElements.forEach((marker, index) => {
    marker.classList.toggle(toggleClass, index === 0);
    extraToggleClass && marker.classList.toggle(extraToggleClass, index === 0);
  });
};
